<template>
  <div class="change-event-families-container">
    <form @submit.prevent="handleSubmit">
      <h4
        class="title">
        {{ $t('CP.familiesSettings') }}
      </h4>
      <div
        class="new-family"
        :class="{ 'dragged-over': isCreateFamilyDraggedOver }"
      >
        <draggable
          class="drag-container"
          group="people"
          filter=".new-family-title"
          :prevent-on-filter="false"
          @add="(e) => {addFamily(e)}"
        >
          <h6 v-if="!isCreateFamilyDraggedOver" class="family-title new-family-title">
            {{ $t('CP.newFamily') }}
          </h6>
        </draggable>
      </div>
      <div
        v-for="(family, index) of families"
        :key="`${family[0].uid}${index}`"
        class="family-container"
      >
        <h6 class="family-title">
          {{ $t('CP.family') }} {{ index + 1 }}:
        </h6>
        <people-list
          @updateFamily="(newFamily) => {updateFamily(newFamily, index)}"
          @draggedOver="(to) => {draggedOver(to)}"
          @dragIsOver="isCreateFamilyDraggedOver = false"
          class="people-list"
          :owner-id="event.owner_id"
          :family-owner-ids="[family[0].uid]"
          message="dont show"
          :disable-focus="true"
          :show-user-phone="true"
          :users="family"
          :show-add-button="false"
          :show-delete-button="false"
          :is-draggable="true"
          :show-drag-icon="true"
          draggable-group="people"
        />
      </div>
      <div class="not-in-families">
        <h6 class="family-title">
          {{ $t('CP.notInFamily') }}:
        </h6>
        <people-list
          class="people-list"
          @updateFamily="(newUsers) => {updateAvailableUsers(newUsers)}"
          @draggedOver="(to) => {draggedOver(to)}"
          @dragIsOver="isCreateFamilyDraggedOver = false"
          message="dont show"
          :disable-focus="true"
          :show-user-phone="true"
          :users="availableUsers"
          :show-add-button="false"
          :show-delete-button="false"
          :owner-id="event.owner_id"
          :is-draggable="true"
          :show-drag-icon="true"
          draggable-group="people"
        />
      </div>
      <div
        class="form-buttons">
        <button-app
          @clicked="cancelSubmit"
          type="button"
          class="form-button form-button_left"
          color-scheme="gray"
        >
          {{ $t('CP.cancel') }}
        </button-app>
        <button-app
          :disabled="false"
          type="submit"
          class="form-button"
          color-scheme="blue"
        >
          {{ $t('CP.save') }}
        </button-app>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Draggable from 'vuedraggable';
import { UPDATE_EVENT_REQUEST } from '@/store/events/types';
import { productTours } from '@/plugins/shepherd';
import ButtonApp from '../../components/Button/ButtonApp';
import PeopleList from '../../components/PeopleList/PeopleList';

export default {
  name: 'DragChangeEventFamilies',
  components: {
    PeopleList,
    ButtonApp,
    Draggable,
  },
  data() {
    return {
      families: [],
      availableUsers: [],
      isCreateFamilyDraggedOver: false,
      breakpointLg: window.matchMedia('(min-width: 1025px)'),
    };
  },
  mounted() {
    this.families = this.searchFamilies();
    this.availableUsers = this.searchAvailableUsers();
    this.showTour();
  },
  computed: {
    ...mapState({
      familiesIds: (state) => state.events.event.families || [],
      event: (state) => state.events.event,
      user: (state) => state.user.user || {},
      areAllToursFinished: (state) => state.tour.areAllToursFinished,
      tourStates: (state) => state.tour.tourStates,
    }),
  },
  methods: {
    showTour() {
      if (!this.areAllToursFinished
        && !this.tourStates.eventFamilies
        && this.breakpointLg.matches) {
        setTimeout(() => {
          productTours[this.$i18n.locale].eventFamilies.start();
        }, 350);
      }
    },
    draggedOver(to) {
      this.isCreateFamilyDraggedOver = false;
      if (to.classList.contains('drag-container')) {
        this.isCreateFamilyDraggedOver = true;
      }
    },
    searchFamilies() {
      return this.familiesIds.map((family) => (
        [
          this.getContactById(family.head),
          ...family.members.map((member) => this.getContactById(member)),
        ]
      ));
    },
    searchAvailableUsers() {
      try {
        return this.event.contacts.filter((contact) => (
          !this.families.some((family) => {
            if (family && family.length) {
              return !!family.find((user) => contact.uid === user.uid);
            }
            return false;
          })
        ));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('family available users error: ', e.message);
        return [];
      }
    },
    addFamily(e) {
      // eslint-disable-next-line no-underscore-dangle
      this.families.push([e.item._underlying_vm_]);
    },
    updateFamily(newFamily, index) {
      if (newFamily.length) {
        this.families.splice(index, 1, newFamily);
      } else {
        this.families.splice(index, 1);
      }
      this.availableUsers = this.searchAvailableUsers();
    },
    updateAvailableUsers(newUsers) {
      if (newUsers && newUsers.length) {
        this.availableUsers = newUsers;
      } else {
        this.availableUsers = [];
      }
    },
    getContactById(id) {
      try {
        const inEventContacts = this.event.contacts.find((contact) => contact.uid === id);
        const inFriends = this.$store.state.user.userContacts.find((contact) => contact.uid === id);
        return inEventContacts || inFriends;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('can not find contact: ', e.message);
        return '';
      }
    },
    handleSubmit() {
      const families = this.families.map((family) => (
        family.reduce((familyAccumulator, member, index) => {
          if (index === 0) {
            // eslint-disable-next-line no-param-reassign
            familyAccumulator.head = member.phone;
          } else {
            familyAccumulator.members.push(member.phone);
          }
          return familyAccumulator;
        }, { head: '', members: [] })
      ));
      this.$store.dispatch(UPDATE_EVENT_REQUEST, {
        id: this.event.id,
        data: {
          families,
        },
      });
      this.$bvModal.hide('change-event-families');
    },
    cancelSubmit() {
      this.$bvModal.hide('change-event-families');
    },
  },
};
</script>

<style scoped>
.change-event-families-container {
  color: var(--text-main-color);
}

.title {
  margin-bottom: 20px;
}

.new-family {
  border: 2px dashed var(--text-seccond-color);
  border-radius: 8px;
  margin-bottom: 8px;
  height: 79px;
}

.drag-container {
  top: 0;
  bottom: 0;
  padding: 10px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  list-style-type: none;
  text-align: center;
}

.dragged-over {
  transition: box-shadow .2s;
  border-color: var(--text-main-color);
  box-shadow: 0 0 50px -30px var(--text-seccond-color);
}

.family-title {
  color: var(--text-seccond-color);
}

.new-family-title {
  margin: 0;
}

.people-list {
  margin-bottom: 8px;
  padding: 0 10px;
}

.form-buttons {
  display: flex;
}

.form-button {
  font-size: 13px;
  font-weight: bold;
  height: 38px;
  flex: 1 0 40%;
  margin: 0;
}

.form-button_left {
  margin: 0 8px 0 0;
}
</style>
